import React, { useContext, useEffect } from 'react';
import AuthContext from '../../store/auth-context';
import {
    FeedbackSurvey,
    getAvailableFeedbackSurveys,
} from '../../services/api/feedbackSurveyService';

declare global {
    interface Window {
        Tally?: {
            openPopup: (formId: string, options: Record<string, any>) => void;
        };
    }
}

const TALLY_SCRIPT_ID = 'tally-popup-script';
const DEFAULT_POPUP_OPTIONS = {
    emojiText: '👋',
    emojiAnimation: 'wave',
    layout: 'default',
    width: 520,
    overlay: false,
    alignLeft: true,
};

const loadTallyScript = () =>
    new Promise<void>((resolve, reject) => {
        if (window.Tally?.openPopup) {
            resolve();
            return;
        }

        const existingScript = document.getElementById(TALLY_SCRIPT_ID);
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(), {
                once: true,
            });
            existingScript.addEventListener('error', () => reject(), {
                once: true,
            });
            return;
        }

        const script = document.createElement('script');
        script.id = TALLY_SCRIPT_ID;
        script.src = 'https://tally.so/widgets/embed.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.body.appendChild(script);
    });

const getSurveyActivePeriodKey = (survey: FeedbackSurvey) =>
    [survey._id, survey.startDate ?? 'no-start', survey.endDate ?? 'no-end'].join(
        ':'
    );

const getSurveyStorageKey = (survey: FeedbackSurvey) =>
    `feedback-survey-submitted:${getSurveyActivePeriodKey(survey)}`;

const getLoginSessionKey = (token?: string) => {
    if (!token) {
        return 'anonymous';
    }

    let hash = 0;
    for (let index = 0; index < token.length; index += 1) {
        hash = (hash * 31 + token.charCodeAt(index)) >>> 0;
    }

    return hash.toString(16);
};

const getSurveySessionDismissedKey = (
    survey: FeedbackSurvey,
    loginSessionKey: string
) =>
    `feedback-survey-dismissed-this-session:${loginSessionKey}:${getSurveyActivePeriodKey(survey)}`;

const clearSessionDismissedSurveys = () => {
    Object.keys(sessionStorage)
        .filter((key) => key.startsWith('feedback-survey-dismissed-this-session:'))
        .forEach((key) => sessionStorage.removeItem(key));
};

const FeedbackSurveyPopupRunner: React.FC = () => {
    const authContext = useContext(AuthContext);

    useEffect(() => {
        const context = authContext.getContext();

        if (!context?.isLoggedIn) {
            clearSessionDismissedSurveys();
            return;
        }

        const loginSessionKey = getLoginSessionKey(context.token);
        let cancelled = false;

        const openSurveyPopup = async () => {
            const surveys = await getAvailableFeedbackSurveys();
            const survey = surveys.find(
                (item) =>
                    !localStorage.getItem(getSurveyStorageKey(item)) &&
                    !sessionStorage.getItem(
                        getSurveySessionDismissedKey(item, loginSessionKey)
                    )
            );

            if (!survey || cancelled) {
                return;
            }

            await loadTallyScript();

            if (!window.Tally?.openPopup || cancelled) {
                return;
            }

            const popupOptions = {
                ...DEFAULT_POPUP_OPTIONS,
                ...survey.popupOptions,
                layout: 'default',
                width: DEFAULT_POPUP_OPTIONS.width,
                overlay: false,
                alignLeft: true,
            };

            window.Tally.openPopup(survey.formId, {
                key: getSurveyStorageKey(survey),
                layout: popupOptions.layout,
                width: popupOptions.width,
                alignLeft: popupOptions.alignLeft,
                overlay: popupOptions.overlay,
                showOnce: false,
                doNotShowAfterSubmit: true,
                emoji: {
                    text: popupOptions.emojiText,
                    animation: popupOptions.emojiAnimation,
                },
                hiddenFields: {
                    departmentId: context.department?._id,
                    departmentName: context.department?.name,
                    role: context.actor?.role,
                    region: context.region,
                },
                onSubmit: () => {
                    localStorage.setItem(getSurveyStorageKey(survey), 'true');
                },
                onClose: () => {
                    sessionStorage.setItem(
                        getSurveySessionDismissedKey(survey, loginSessionKey),
                        'true'
                    );
                },
            });
        };

        openSurveyPopup().catch((error) => {
            console.error('Failed to open feedback survey popup', error);
        });

        return () => {
            cancelled = true;
        };
    }, [authContext]);

    return null;
};

export default FeedbackSurveyPopupRunner;
